import {Component, OnInit, Inject} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {FormGroup, FormControl} from '@angular/forms';
import {HttpLoaderService, ProductTypeEnum} from '@tymes4-shared';
import {TranslateService} from '@ngx-translate/core';
import {
  AssignmentPolicy,
  AssignmentPolicyService,
  CalculatedPriceVariantModel,
  EntranceService,
  PendingOrderLineService,
  PendingOrderLineViewWithPricing,
  PutOrderlinePropertiesArgs, TicketText,
  TicketTextService,
  VenueBuildingBlockEntranceView
} from '../../api';
import {ApplicationModuleHelperService} from '../../services/application-module.service';

interface AssignmentPolicyWithBindName extends AssignmentPolicy {
  BindName?: string;
}

@Component({
    selector: 'app-edit-orderline-form',
    templateUrl: './edit-orderline-form.component.html',
    standalone: false
})
export class EditOrderLineFormComponent implements OnInit {
  
  public form!: FormGroup;
  public ticketTexts: TicketText[] = [];
  public selectableEntrances: VenueBuildingBlockEntranceView[] = [];
  private selectedOrderLines: PendingOrderLineViewWithPricing[] = [];
  public priceVariants: CalculatedPriceVariantModel[] = [];
  public assignmentPolicies: AssignmentPolicyWithBindName[] = [];
  public showAssignmentPolicies: boolean = false;
  public saving: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public passedData: any,
              public dialogRef: MatDialogRef<EditOrderLineFormComponent>,
              private entranceService: EntranceService,
              private ticketTextService: TicketTextService,
              private pendingOrderLineService: PendingOrderLineService,
              private assignmentPolicyService: AssignmentPolicyService,
              private loader: HttpLoaderService,
              private translate: TranslateService,
              private applicationModuleHelperService: ApplicationModuleHelperService) {
  }

  ngOnInit() {
    this.selectedOrderLines = this.passedData.payload;
    this.retrieveSelectableEntrances();
    this.retrieveTicketTexts();

    this.determineAssignmentPolicyCompatibility();

    this.form = new FormGroup({
      EntranceId: new FormControl(-1), //select the "niet aanpassen" as default
      TicketTextId: new FormControl(-1), //select the "niet aanpassen" as default
      TicketPrice: new FormControl(""),
      PriceVariationId: new FormControl(-1), //select the "niet aanpassen" as default
      AssignmentPolicyId: new FormControl(-1), //select the "niet aanpassen" as default
    });

    this.form.patchValue(this.selectedOrderLines[0]);
  }

  retrieveTicketTexts() {
    this.loader.open();

    this.ticketTextService.ticketTextGetAll().subscribe((texts: TicketText[]) => {
      texts.unshift({
        Id: -999,
        Text: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.STANDARD')
      });

      texts.unshift({
        Id: -1,
        Text: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.DONT-CHANGE')
      });

      this.ticketTexts = texts;

      this.loader.close();
    });
  }

  retrieveSelectableEntrances() {

    this.loader.open();

    //get the distinct vbb ids
    let parentVbbIds: number[] = Array.from(new Set(this.selectedOrderLines.map((item: PendingOrderLineViewWithPricing) => item.ParentVenueBuildingBlockId).filter((x): x is number => x !== null && x !== undefined)));

    parentVbbIds = parentVbbIds.filter((x: number) => x !== null);
    this.entranceService.getAvailableInSections(parentVbbIds).subscribe((entrances: VenueBuildingBlockEntranceView[]) => {

      const allEntrances = entrances;

      //add the default
      allEntrances.splice(0, 0, {
        EntranceId: -999,
        EntranceName: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.STANDARD')
      });

      //add no change
      allEntrances.splice(0, 0, {
        EntranceId: -1,
        EntranceName: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.DONT-CHANGE')
      });

      this.selectableEntrances = allEntrances;
      this.loader.close();
      this.retrieveSelectablePriceVariation();
    });
  }

  retrieveSelectablePriceVariation() {

    this.loader.open();
    const allPriceVariations = this.passedData.AllPriceVariants;

    //add no change
    if (allPriceVariations.length > 0 && allPriceVariations[0].VariantId !== -1) {
      allPriceVariations.splice(0, 0, {
        VariantId: -1,
        VariationName: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.DONT-CHANGE')
      });
    }

    this.priceVariants = allPriceVariations;
    this.loader.close();
  }

  retrieveAllPolicies() {
    this.loader.open();
    this.assignmentPolicyService.listAssignmentPolicies().subscribe((policies: AssignmentPolicy[]) => {

      policies.forEach((policy: AssignmentPolicyWithBindName) => {
        policy.BindName = policy.Name + ' - ' + policy.Code;
      });

      policies.unshift({
        Id: -1,
        Name: this.translate.instant('CUSTOM.BUTTON.STATE.LIST.DONT-CHANGE')
      });

      this.assignmentPolicies = policies;
      this.loader.close();
    })
  }

  submit() {
    const propertyObject = this.form.value;

    this.saving = true;
    this.loader.open();

    // reset to default value
    if (propertyObject.EntranceId == -999) {
      propertyObject.EntranceId = null;
    }
    if (propertyObject.TicketTextId == -999) {
      propertyObject.TicketTextId = null;
    }
    if (propertyObject.PriceVariationId == -999) {
      propertyObject.PriceVariationId = null;
    }

    propertyObject.SelectedOlIds = this.selectedOrderLines.map((item: PendingOrderLineViewWithPricing) => item.Id);

    const args: PutOrderlinePropertiesArgs = {
      SelectedOlIds: propertyObject.SelectedOlIds,
      TicketTextId: propertyObject.TicketTextId,
      PriceVariationId: propertyObject.PriceVariationId,
      EntranceId: propertyObject.EntranceId,
      AssignmentPolicyId: this.showAssignmentPolicies ? propertyObject.AssignmentPolicyId : null,
    };

    // add the selected orderlines to the property object
    this.pendingOrderLineService.putPendingOrderLineProperties(args).subscribe((_: PendingOrderLineViewWithPricing) => {
      // close this dialog and pass the newly added id to the called
      this.dialogRef.close(true);
      this.saving = false;
      this.loader.close();
    });
  }

  determineAssignmentPolicyCompatibility() {
    this.applicationModuleHelperService.getActiveModules().subscribe((activeModules) => {
      if (activeModules === null || !activeModules.some((module) => module.toUpperCase() == 'IBA')) {
        return;
      }

      if (this.selectedOrderLines.some(orderLine => orderLine.ProductType != ProductTypeEnum.Ticket || orderLine.PassePartoutId)) {
        return;
      }

      this.showAssignmentPolicies = true;
      this.retrieveAllPolicies();
    })
  }
}
